import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import './DataTableForm.scss';
import {
    DataTable,
    DataTableColumn,
    DataTableRow,
    DataTableProgressStatusList,
    DataTableViewProgressStatus,
    TableViewProgressStatusList,
    DataTableProgressStatus,
    isCellAvailableByLogic,
    isTableColumnVisibleByLogic,
    DataTableView,
    isRowAvailableByViewFilter,
} from '../../../models/DataRequestHub/DataTable';
import {
    GridCellParams,
    GridColDef,
    GridColumnHeaderParams,
    GridEditDateCell,
    GridPreProcessEditCellProps,
    GridRowModel,
    GridValidRowModel,
    useGridApiRef,
} from '@mui/x-data-grid';
import FreezePaneDataGrid from '../../../components/FreezePaneDataGrid/FreezePaneDataGrid';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { CustomIdConstants } from '../../../models/DataRequestHub/CustomIdConstants';
import DataTableFormActionColumn from './DataTableFormActionColumn';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import {
    AnswerTypeVariable,
    AnswerTypeVariableNamesList,
    FormTypeVariable,
    FormTypeVariableNamesList,
    ProjectVariable,
} from '../../../models/DataRequestHub/ProjectVariable';
import { objectToQueryString } from '../../../utils/queryHelper';
import { NumericCell } from './NumericCell/NumericCell';
import moment from 'moment';
import { TimeZoneHelper } from '../../../utils/timeZoneHelper';
import { Box, TablePagination } from '@mui/material';
import { Badge, Button } from 'reactstrap';
import { ResetCellInfo } from '../../../models/DataRequestHub/slices/data-request-notifications.slice';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompressAlt, faExpandAlt } from '@fortawesome/free-solid-svg-icons';
import TextInputColumn from '../../../components/EditableTable/CustomColumns/TextInputColumn/TextInputColumn';
import { wrapInPromise } from '../../../utils/WrapInPromise';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import PortalTooltip from '../../../components/PortalTooltip/PortalTooltip';
import { resetCellSeenStatusByIds } from '../../../models/DataRequestHub/slices/thunks/reset-cell-seen-status-by-ids.thunk';
import { saveDataTableColumnWidth } from '../../../models/DataRequestHub/slices/thunks/save-data-table-column-widths.thunk';
import { saveFreezePanesSettings } from '../../../models/DataRequestHub/slices/thunks/save-freeze-panes-settings.thunk';
import { saveDataTableFreezePanesSettings } from '../../../models/DataRequestHub/slices/thunks/save-data-table-freeze-panes-settings.thunk';
import { FreezePanesSettings } from '../../../models/DataRequestHub/slices/models/freeze-panes-settings.model';
import { ColDefRenderCell } from './ColumnDefinitionComponents/ColDefRenderCell';
import { DataRequestProject } from '../../../models/DataRequestHub/DataRequestProject';
import { useIsDataRequestProjectReadonly } from '../../../custom-hooks/useIsDataRequestProjectReadonly';
import ReactSwitch from 'react-switch';
import { DataTableCellUploadPopup } from '../DataTableCellUploadPopup/DataTableCellUploadPopup';

interface DataTableFormProps {
    projectId: number;
    project: DataRequestProject;
    variables: ProjectVariable[];
    dataTable: DataTable;
    selectedTableViewId: number;
    isAdminView: boolean;
    updateProjectsData(isInitialFetch?: boolean): void;
    markCellsAsSeen(projectId: number, tableId: number, viewId: number): void;
    updateColumnWidth?(tableId: number, columnId: string, width: number): void;
    updateFreezePanes?(tableId: number, viewId: number, settings: FreezePanesSettings): void;
    updateDataTableFreezePanes?(tableId: number, settings: FreezePanesSettings): void;
    columnWidthOverrides?: ResizedColumnDefinition[];
    isReadonlyMode: boolean;
    isSaveAndCloseVisible?: boolean;
    onSaveAndCloseClick?(): void;
    areActionButtonsHidden?: boolean;
    isFullScreenButtonHidden?: boolean;
}

interface ResizedColumnDefinition {
    columnId: string;
    width: number;
}

export const DataTableForm = (props: DataTableFormProps) => {
    const apiRef = useGridApiRef();
    const [tableData, setTableData] = useState<DataTableRow[]>([]);
    const axios = useStateSelector((s) => s.core.axios);
    const [dataTable, setDataTable] = useState<DataTable>(null);
    const [isRequestPending, setIsRequestPending] = useState(false);
    const [isStatusUpdateInProcess, setIsStatusUpdateInProcess] =
        useState(false);
    const [columnResized, setResizedColumns] = useState<
        ResizedColumnDefinition[]
    >([]);
    const [fullScreenEnabled, setFullScreenEnabled] = useState(false);
    const [markAsCompleteState, setMarkAsCompleteState] = useState(false);
    const [isMarkAsCompleteModified, setIsMarkAsCompleteModified] =
        useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isForceUpdate, setIsForceUpdate] = useState(false);
    const abortControllerRef = useRef(null);
    const pendingUpdateRef = useRef(false);
    const tableNotificationStatus = useStateSelector((s) =>
        s.dataRequestsNotifications.tablesNotificationsStatus.find(
            (f) => f.tableId === props.dataTable.id
        )
    );
    const isAddNewRowPending = useRef(false);
    const isUpdateRowsPending = useRef(false);
    const isReadonlyProject = useIsDataRequestProjectReadonly([props.project]);

    const [selectedCell, setSelectedCell] = useState<{ columnId: number; rowId: number } | null>(null);
    const [isFilePopupVisible, setIsFilePopupVisible] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });

    const [frozenColumnCount, setFrozenColumnCount] = useState(0);
    const [frozenRowCount, setFrozenRowCount] = useState(0);
    const [frozenColumnStart, setFrozenColumnStart] = useState(0);
    const [frozenRowStart, setFrozenRowStart] = useState(0);
    const [scrollableColumnStart, setScrollableColumnStart] = useState<number | undefined>(undefined);
    const [scrollableRowStart, setScrollableRowStart] = useState<number | undefined>(undefined);
    const [selectedFreezeCell, setSelectedFreezeCell] = useState<GridCellParams | null>(null);
    const [freezeDropdownLabel, setFreezeDropdownLabel] = useState('Freeze Panes');

    const getFreezePanesLabel = (settings: FreezePanesSettings): string => {
        const hasFrozenColumns = (settings.frozenColumnCount || 0) > 0;
        const hasFrozenRows = (settings.frozenRowCount || 0) > 0;

        if (!hasFrozenColumns && !hasFrozenRows) return 'Freeze Panes';

        if (hasFrozenColumns && hasFrozenRows) return 'Freeze at selection';

        if (hasFrozenColumns) return `Up To Column ${settings.frozenColumnCount}`;

        return `Up To Row ${settings.frozenRowCount}`;
    };
    const getVisibleRangesRef = useRef<(() => { firstColumnIndex: number; lastColumnIndex: number; firstRowIndex: number; lastRowIndex: number }) | null>(null);
    const [capturedVisibleRanges, setCapturedVisibleRanges] = useState<{ firstColumnIndex: number; lastColumnIndex: number; firstRowIndex: number; lastRowIndex: number } | null>(null);
    const [freezeScrollToCell, setFreezeScrollToCell] = useState<{ id: any; field: string; viewportOffset?: { top: number; left: number } } | null>(null);
    const isInitialLoadRef = useRef(true);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            const formData = new FormData();
            formData.append('file', file);

            axios
                .post(
                    `/api/dataRequestProjects/${props.projectId}/dataTables/${props.dataTable.id}/dataTableSnapshots/file`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                )
                .then((response) => {
                    if (response.status !== 200) {
                        const message = (response as any)?.response?.data
                            ?.detail;
                        createNotification(
                            message ??
                                'An error occurred while processing file',
                            'error'
                        );
                    }
                    fileInputRef.current.value = '';
                    props.updateProjectsData();
                });
        }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const dispatch: any = useDispatch();

    const debouncedSaveColumnWidth = useMemo(
        () =>
            debounce(
                (columnId: string, width: number) => {
                    dispatch(
                        saveDataTableColumnWidth({
                            projectId: props.projectId,
                            dataTableId: props.dataTable.id,
                            columnId,
                            width,
                        })
                    );
                },
                400
            ),
        [props.projectId, props.dataTable?.id, dispatch]
    );

    useEffect(() => {
        return () => debouncedSaveColumnWidth.cancel();
    }, [debouncedSaveColumnWidth]);

    const debouncedSaveFreezePanesSettings = useMemo(
        () =>
            debounce(
                (settings: {
                    frozenColumnCount: number;
                    frozenRowCount: number;
                    frozenColumnStart?: number;
                    frozenRowStart?: number;
                    scrollableColumnStart?: number;
                    scrollableRowStart?: number;
                }) => {
                    if (props.selectedTableViewId) {
                        dispatch(
                            saveFreezePanesSettings({
                                projectId: props.projectId,
                                dataTableId: props.dataTable.id,
                                dataTableViewId: props.selectedTableViewId,
                                settings,
                            })
                        );
                    }
                },
                200
            ),
        [props.projectId, props.dataTable?.id, props.selectedTableViewId, dispatch]
    );

    useEffect(() => {
        return () => debouncedSaveFreezePanesSettings.cancel();
    }, [debouncedSaveFreezePanesSettings]);

    const debouncedSaveDataTableFreezePanesSettings = useMemo(
        () =>
            debounce(
                (settings: {
                    frozenColumnCount: number;
                    frozenRowCount: number;
                    frozenColumnStart?: number;
                    frozenRowStart?: number;
                    scrollableColumnStart?: number;
                    scrollableRowStart?: number;
                }) => {
                    dispatch(
                        saveDataTableFreezePanesSettings({
                            projectId: props.projectId,
                            dataTableId: props.dataTable.id,
                            settings,
                        })
                    );
                },
                200
            ),
        [props.projectId, props.dataTable?.id, dispatch]
    );

    useEffect(() => {
        return () => debouncedSaveDataTableFreezePanesSettings.cancel();
    }, [debouncedSaveDataTableFreezePanesSettings]);

    useEffect(() => {
        if (props.selectedTableViewId) {
            const currentView = getCurrentTableView();
            isInitialLoadRef.current = true;
            if (currentView?.freezePanesSettings) {
                const settings = currentView.freezePanesSettings;
                setFrozenColumnCount(settings.frozenColumnCount || 0);
                setFrozenRowCount(settings.frozenRowCount || 0);
                setFrozenColumnStart(settings.frozenColumnStart ?? 0);
                setFrozenRowStart(settings.frozenRowStart ?? 0);
                setScrollableColumnStart(settings.scrollableColumnStart);
                setScrollableRowStart(settings.scrollableRowStart);
                setFreezeDropdownLabel(getFreezePanesLabel(settings));
            } else {
                const defaultSettings: FreezePanesSettings = {
                    frozenColumnCount: 0,
                    frozenRowCount: 0,
                    frozenColumnStart: 0,
                    frozenRowStart: 0,
                    scrollableColumnStart: 1,
                };
                setFrozenColumnCount(defaultSettings.frozenColumnCount);
                setFrozenRowCount(defaultSettings.frozenRowCount);
                setFrozenColumnStart(defaultSettings.frozenColumnStart);
                setFrozenRowStart(defaultSettings.frozenRowStart);
                setScrollableColumnStart(defaultSettings.scrollableColumnStart);
                setScrollableRowStart(defaultSettings.scrollableRowStart);
                setFreezeDropdownLabel('Freeze Panes');
                setDataTable((prevDataTable) => {
                    const baseTable = prevDataTable || props.dataTable;
                    if (!baseTable) return prevDataTable;
                    const updatedViews = baseTable.views.map((view) => {
                        if (view.id === props.selectedTableViewId) {
                            return {
                                ...view,
                                freezePanesSettings: defaultSettings,
                            };
                        }
                        return view;
                    });
                    return {
                        ...baseTable,
                        views: updatedViews,
                    };
                });
                props.updateFreezePanes?.(props.dataTable.id, props.selectedTableViewId, defaultSettings);
                setTimeout(() => {
                    debouncedSaveFreezePanesSettings(defaultSettings);
                }, 200);
            }
            setTimeout(() => {
                isInitialLoadRef.current = false;
            }, 100);
        } else {
            isInitialLoadRef.current = true;
            const tableSettings = (dataTable || props.dataTable)?.freezePanesSettings;
            if (tableSettings) {
                setFrozenColumnCount(tableSettings.frozenColumnCount || 0);
                setFrozenRowCount(tableSettings.frozenRowCount || 0);
                setFrozenColumnStart(tableSettings.frozenColumnStart ?? 0);
                setFrozenRowStart(tableSettings.frozenRowStart ?? 0);
                setScrollableColumnStart(tableSettings.scrollableColumnStart);
                setScrollableRowStart(tableSettings.scrollableRowStart);
                setFreezeDropdownLabel(getFreezePanesLabel(tableSettings));
            } else {
                const defaultSettings: FreezePanesSettings = {
                    frozenColumnCount: 0,
                    frozenRowCount: 0,
                    frozenColumnStart: 0,
                    frozenRowStart: 0,
                    scrollableColumnStart: 1,
                };
                setFrozenColumnCount(defaultSettings.frozenColumnCount);
                setFrozenRowCount(defaultSettings.frozenRowCount);
                setFrozenColumnStart(defaultSettings.frozenColumnStart);
                setFrozenRowStart(defaultSettings.frozenRowStart);
                setScrollableColumnStart(defaultSettings.scrollableColumnStart);
                setScrollableRowStart(defaultSettings.scrollableRowStart);
                setFreezeDropdownLabel('Freeze Panes');
                setDataTable((prevDataTable) => {
                    const baseTable = prevDataTable || props.dataTable;
                    if (!baseTable) return prevDataTable;
                    return {
                        ...baseTable,
                        freezePanesSettings: defaultSettings,
                    };
                });
                props.updateDataTableFreezePanes?.(props.dataTable.id, defaultSettings);
                setTimeout(() => {
                    debouncedSaveDataTableFreezePanesSettings(defaultSettings);
                }, 200);
            }
            setTimeout(() => {
                isInitialLoadRef.current = false;
            }, 100);
        }
    }, [props.selectedTableViewId, props.dataTable?.id, props.dataTable?.views]);

    const getCurrentTableView = () => {
        const tableToUse = dataTable || props.dataTable;
        return tableToUse.views.find((x) => x.id === props.selectedTableViewId);
    };

    const getCurrentTableViewColumns = () => {
        const selectedView = getCurrentTableView();

        const columnIds =
            selectedView?.viewColumns
                .sort(
                    (a, b) => a.overridenDisplayOrder - b.overridenDisplayOrder
                )
                .filter((viewColumn) => viewColumn.isVisible)
                .map((viewColumn) => viewColumn.customColumnId) ??
            props.dataTable.columns
                .filter((column) => {
                    return isTableColumnVisibleByLogic(
                        column,
                        props.dataTable.columns
                    );
                })
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((tableColumn) => tableColumn.customColumnId);

        return columnIds
            .map((id) => {
                return props.dataTable.columns.find(
                    (tableColumn) =>
                        tableColumn.customColumnId === id &&
                        tableColumn.isVisible
                );
            })
            .filter((x) => x != null);
    };

    const getVariableOptionsByName = (name: string) => {
        const variable = props.variables.find((x) => x.name === name);
        if (variable == null) {
            return [];
        }
        const result = variable.options.map((x) => x.option);
        return result;
    };

    const isReadonlyAccess = () => {
        const isTableComplete =
            props.dataTable.progressStatus === DataTableProgressStatus.Complete;
        const isCurrentViewConfirmed =
            getCurrentTableView()?.progressStatus ===
            DataTableViewProgressStatus.Complete;

        return isTableComplete || isCurrentViewConfirmed || isReadonlyProject;
    };

    const getRowId = (row: GridValidRowModel) => row.customRowId;

    const getFileColumn = (columnDefinition: DataTableColumn): GridColDef => {
        const isResized = isColumnResized(columnDefinition.customColumnId);
        return {
            field: columnDefinition.customColumnId,
            headerName: columnDefinition.name,
            minWidth: 50,
            width: isResized ? getColumnResizedWidth(columnDefinition.customColumnId) : 150,
            editable: false,
            renderCell: (params) => (
                <div className="file-cell">
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => {
                            setSelectedCell({
                                columnId: columnDefinition.id,
                                rowId: params.row.id,
                            });
                            setIsFilePopupVisible(true);
                        }}>
                        Files
                    </Button>
                </div>
            ),
            renderHeader: (params: GridColumnHeaderParams) =>
                getColumnHeader(params?.field, params?.colDef.headerName, columnDefinition),
        } as GridColDef;
    };

    const dataColumns = (): GridColDef[] => {
        const viewColumns = getCurrentTableViewColumns().map((x) => {
            switch (x.answerType) {
                case AnswerTypeVariableNamesList.find(
                    (u) => u.id === AnswerTypeVariable.PickFromList
                ).name:
                    return getDropDownColumn(x);
                case AnswerTypeVariableNamesList.find(
                    (u) => u.id === AnswerTypeVariable.Number
                ).name:
                    return getNumericColumn(x);

                case AnswerTypeVariableNamesList.find(
                    (u) => u.id === AnswerTypeVariable.Date
                ).name:
                    return getDateColumn(x);
                case AnswerTypeVariableNamesList.find(
                    (u) => u.id === AnswerTypeVariable.Text
                ).name:
                    return getTextInputColumn(x);

                case AnswerTypeVariableNamesList.find(
                    (u) => u.id === AnswerTypeVariable.File
                ).name:
                    return getFileColumn(x);

                default:
                    return getTextInputColumn(x);
            }
        });

        if (props.isAdminView) {
            return viewColumns;
        }

        const anyNewRows = tableNotificationStatus?.rowsSeenStatus?.some(
            (rowStatus) => !rowStatus.isSeenByUser
        );

        const isNewTableView =
            !tableNotificationStatus?.viewsNotificationsStatus?.find(
                (f) => f.viewId === props.selectedTableViewId
            )?.isSeenByUser;

        const notificationColumn: GridColDef = {
            field: 'id',
            headerName: '',
            minWidth: 50,
            maxWidth: 50,
            width: 50,
            flex: 0.5,
            type: 'string',
            editable: false,
            renderCell: (params) => {
                const targetRow = tableNotificationStatus.rowsSeenStatus.find(
                    (row) => params.row.customRowId === row.customRowId
                );

                return targetRow && !targetRow.isSeenByUser ? (
                    <Box className="MuiDataGrid-cellContent custom-render-cell">
                        <Badge className="cell-notification-badge unread-comments-badge">
                            1
                        </Badge>
                    </Box>
                ) : (
                    <></>
                );
            },
            sortable: false,
        } as GridColDef;

        return anyNewRows &&
            !props.isAdminView &&
            !tableNotificationStatus.isNewProject &&
            !tableNotificationStatus.isNew &&
            !isNewTableView
            ? [notificationColumn, ...viewColumns]
            : viewColumns;
    };

    const isCellAvailable = (targetColumn: DataTableColumn, gridRow: any) => {
        const viewColumnsIds = props.selectedTableViewId
            ? props.dataTable.views.flatMap((view) =>
                  view.viewColumns.map(
                      (viewColumn) => viewColumn.customColumnId
                  )
              )
            : props.dataTable.columns.map(
                  (tableColumn) => tableColumn.customColumnId
              );

        const celanRows = clearRowsFromCellProperties([gridRow]).find((f) => f);
        const result = isCellAvailableByLogic(
            targetColumn,
            celanRows,
            viewColumnsIds,
            props.dataTable.columns
        );
        return result;
    };

    const getColumnHeader = (
        field: string,
        headerName: string,
        customColumn: DataTableColumn
    ) => (
        <>
            {getTooltip(
                `${props.dataTable.id}-header-${field}`,
                <>{headerName}</>
            )}
            <div className="grid-column-name-wrapper">
                <div
                    id={`${props.dataTable.id}-header-${field}`}
                    className="grid-column-name">
                    {headerName}
                </div>
                {customColumn.columnTooltip && (
                    <InfoTooltip
                        idText={`column-tooltip-${customColumn.customColumnId}`}
                        text={customColumn.columnTooltip}
                        place="bottom"></InfoTooltip>
                )}
            </div>
            {!props.isAdminView ? (
                renderHeaderColumnNotification(customColumn.customColumnId)
            ) : (
                <></>
            )}
        </>
    );

    const isColumnResized = (colId: string): boolean => {
        return columnResized.some((col) => col.columnId === colId);
    };

    const getColumnResizedWidth = (colId: string): number => {
        const column = columnResized.find((col) => col.columnId === colId);

        return column?.width ?? 0;
    };

    const getDateColumn = (columnDefinition: DataTableColumn): GridColDef => {
        const isResized = isColumnResized(columnDefinition.customColumnId);
        let result = {
            field: columnDefinition.customColumnId,
            headerName: columnDefinition.name,
            minWidth: 50,
            width: isResized
                ? getColumnResizedWidth(columnDefinition.customColumnId)
                : 150,
            type: 'date',
            cellClassName: (params) => {
                let resultClasses = 'cell-date-input date-picker tooltip-cell';

                if (!isCellAvailable(columnDefinition, params.row)) {
                    resultClasses = `${resultClasses} disabled`;
                }

                return resultClasses;
            },
            editable: !isReadonlyAccess() && !props.isReadonlyMode,
            valueFormatter: (value: any) =>
                value ? moment(value).format('L') : null,
            valueSetter: (value, row) => {
                const currentValue = row[columnDefinition.customColumnId];
                const newDate = new Date(value);
                if (currentValue && newDate.toDateString === currentValue) {
                    return row;
                }

                const isDateValid = TimeZoneHelper.isDateValid(newDate);
                let dateOnly = '';

                if (isDateValid) {
                    dateOnly = newDate.toDateString();
                }

                return {
                    ...row,
                    [columnDefinition.customColumnId]: dateOnly,
                };
            },
            renderEditCell: (params) => {
                return <GridEditDateCell {...params} />;
            },
            renderCell: (params) => {
                const tooltipContent = (
                    <div>
                        {params?.value
                            ? moment(params?.value).format('L')
                            : null}
                    </div>
                );
                return (
                    <ColDefRenderCell
                        params={params}
                        isCellAvailable={isCellAvailable}
                        columnDefinition={columnDefinition}
                        dataTable={props.dataTable}
                        getCurrentTableView={getCurrentTableView}
                        isAdminView={props.isAdminView}
                        selectedTableViewId={props.selectedTableViewId}
                        tooltipContent={tooltipContent}
                        renerValue={params.formattedValue}
                    />
                );
            },
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !!!params.props.value;
                return { ...params.props, error: hasError };
            },
            renderHeader: (params: GridColumnHeaderParams) =>
                getColumnHeader(
                    params?.field,
                    params?.colDef.headerName,
                    columnDefinition
                ),
        } as GridColDef;

        return result;
    };

    const getNumericColumn = (
        columnDefinition: DataTableColumn
    ): GridColDef => {
        const isResized = isColumnResized(columnDefinition.customColumnId);

        let result = {
            field: columnDefinition.customColumnId,
            headerName: columnDefinition.name,
            minWidth: 50,
            width: isResized
                ? getColumnResizedWidth(columnDefinition.customColumnId)
                : 150,
            cellClassName: (params) => {
                let resultClasses = 'cell-text-input numeric-cell tooltip-cell';

                if (!isCellAvailable(columnDefinition, params.row)) {
                    resultClasses = `${resultClasses} disabled`;
                }

                return resultClasses;
            },
            editable: !isReadonlyAccess() && !props.isReadonlyMode,
            renderEditCell: (params: any) => (
                <NumericCell
                    isReadonly={false}
                    formType={columnDefinition.formType}
                    {...params}
                />
            ),
            renderCell: (params) => {
                const numericCell = (
                    <NumericCell
                        isReadonly={true}
                        formType={columnDefinition.formType}
                        {...params}
                    />
                );

                return (
                    <ColDefRenderCell
                        params={params}
                        isCellAvailable={isCellAvailable}
                        columnDefinition={columnDefinition}
                        dataTable={props.dataTable}
                        getCurrentTableView={getCurrentTableView}
                        isAdminView={props.isAdminView}
                        selectedTableViewId={props.selectedTableViewId}
                        tooltipContent={numericCell}
                        renerValue={numericCell}
                    />
                );
            },

            renderHeader: (params: GridColumnHeaderParams) =>
                getColumnHeader(
                    params?.field,
                    params?.colDef.headerName,
                    columnDefinition
                ),
        } as GridColDef;

        return result;
    };

    const getDropDownColumn = (
        columnDefinition: DataTableColumn
    ): GridColDef => {
        let valueOptions = [''];

        if (
            columnDefinition.formType ===
            FormTypeVariableNamesList.find(
                (u) => u.id === FormTypeVariable.DropDown
            ).name
        ) {
            valueOptions = getVariableOptionsByName(columnDefinition.formList);
        } else if (
            columnDefinition.formType ===
            FormTypeVariableNamesList.find(
                (u) => u.id === FormTypeVariable.YesNo
            ).name
        ) {
            valueOptions = ['Yes', 'No'];
        }
        const isResized = isColumnResized(columnDefinition.customColumnId);

        let result = {
            field: columnDefinition.customColumnId,
            headerName: columnDefinition.name,
            minWidth: 50,
            width: isResized
                ? getColumnResizedWidth(columnDefinition.customColumnId)
                : 150,
            type: 'singleSelect',
            valueOptions: valueOptions,
            cellClassName: (params) => {
                let resultClasses = 'cell-text-input tooltip-cell';

                if (!isCellAvailable(columnDefinition, params.row)) {
                    resultClasses = `${resultClasses} disabled`;
                }

                return resultClasses;
            },
            editable: !isReadonlyAccess() && !props.isReadonlyMode,
            renderCell: (params) => {
                return (
                    <ColDefRenderCell
                        params={params}
                        isCellAvailable={isCellAvailable}
                        columnDefinition={columnDefinition}
                        dataTable={props.dataTable}
                        getCurrentTableView={getCurrentTableView}
                        isAdminView={props.isAdminView}
                        selectedTableViewId={props.selectedTableViewId}
                    />
                );
            },
            renderHeader: (params: GridColumnHeaderParams) =>
                getColumnHeader(
                    params?.field,
                    params?.colDef.headerName,
                    columnDefinition
                ),
        } as GridColDef;

        return result;
    };

    const getTextInputColumn = (
        columnDefinition: DataTableColumn
    ): GridColDef => {
        const isResized = isColumnResized(columnDefinition.customColumnId);

        const result = {
            field: columnDefinition.customColumnId,
            headerName: columnDefinition.name,
            minWidth: 50,
            width: isResized
                ? getColumnResizedWidth(columnDefinition.customColumnId)
                : 150,
            type: 'string',
            cellClassName: (params) => {
                let resultClasses = 'cell-text-input';

                if (!isCellAvailable(columnDefinition, params.row)) {
                    resultClasses = `${resultClasses} disabled`;
                }

                return resultClasses;
            },
            editable: !isReadonlyAccess() && !props.isReadonlyMode,
            valueSetter: (value, row) => {
                return {
                    ...row,
                    [columnDefinition.customColumnId]: value.trim(),
                };
            },
            renderEditCell: (params) => <TextInputColumn {...params} />,
            renderHeader: (params: GridColumnHeaderParams) =>
                getColumnHeader(
                    params?.field,
                    params?.colDef.headerName,
                    columnDefinition
                ),
            renderCell: (params) => {
                return (
                    <ColDefRenderCell
                        params={params}
                        isCellAvailable={isCellAvailable}
                        columnDefinition={columnDefinition}
                        dataTable={props.dataTable}
                        getCurrentTableView={getCurrentTableView}
                        isAdminView={props.isAdminView}
                        selectedTableViewId={props.selectedTableViewId}
                    />
                );
            },
        } as GridColDef;

        return result;
    };

    const getTooltip = (anchorId: string, content: React.JSX.Element) => {
        return (
            <PortalTooltip
                anchorId={anchorId}
                delayShow={1000}
                place="bottom"
                content={content}
            />
        );
    };

    const renderHeaderColumnNotification = (customColumnId: string) => {
        const targetView =
            tableNotificationStatus?.viewsNotificationsStatus?.find(
                (view) => view.viewId === props.selectedTableViewId
            );
        const targetColumn = targetView?.columnsSeenStatus.find(
            (column) => column.customColumnId === customColumnId
        );

        return props.selectedTableViewId &&
            !props.isAdminView &&
            !targetColumn?.isSeenByUser &&
            !tableNotificationStatus?.isNew &&
            !props.dataTable.isSnapshot ? (
            <Badge className="cell-notification-badge unread-comments-badge">
                1
            </Badge>
        ) : (
            <></>
        );
    };

    const resetCellNotifications = (cellIds: ResetCellInfo[]) => {
        dispatch(
            resetCellSeenStatusByIds({
                resetCellsInfo: cellIds,
                tableId: props.dataTable.id,
                viewId: props.selectedTableViewId,
            })
        );
    };

    const updateTableData = () => {
        let rowList = props.dataTable.rows.filter(
            (x) => (x.isVisible && !x.isRemovalRequested) || props.isAdminView
        );

        if (props.selectedTableViewId) {
            const selectedTableView = props.dataTable.views.find(
                (view) => view.id === props.selectedTableViewId
            );
            rowList = filterRowsByViewFilters(rowList, selectedTableView);
        }

        const columns = props.dataTable.columns;

        const result = rowList.map((row) => {
            const newRow = { ...row } as any;

            row.cells.forEach((y) => {
                const column = columns.find(
                    (x) => x.customColumnId === y.customColumnId
                );
                if (column) {
                    newRow[column.customColumnId] = y.answerText;
                }
            });
            return newRow;
        });

        setTableData(result);
    };

    const filterRowsByViewFilters = (
        rows: DataTableRow[],
        view: DataTableView
    ): DataTableRow[] => {
        const result = rows.filter((row) => {
            return isRowAvailableByViewFilter(row, view);
        });

        return result;
    };

    const clearRowsFromCellProperties = (
        updatedRows: DataTableRow[]
    ): DataTableRow[] => {
        return updatedRows.map((updatedRow) => {
            const newCells = updatedRow.cells.map((cell) => {
                const newAnswerText = updatedRow[
                    cell.customColumnId as keyof typeof updatedRow
                ] as string;
                const isNullOrUndefined =
                    newAnswerText === undefined || newAnswerText === null;
                return {
                    ...cell,
                    answerText: isNullOrUndefined
                        ? cell.answerText
                        : newAnswerText,
                };
            });

            return {
                ...updatedRow,
                cells: newCells,
            };
        });
    };

    useEffect(() => {
        if (
            Object.keys(apiRef?.current?.state?.editRows)?.length === 0 &&
            pendingUpdateRef.current
        ) {
            debounceUpdateDataTableRows(tableData);
        }
    }, [apiRef?.current?.state?.editRows]);

    const updateRows = (
        newRows: (oldRows: DataTableRow[]) => DataTableRow[]
    ): void => {
        const result = newRows(tableData);
        setTableData(result);
        debounceUpdateDataTableRows(result);
    };

    const filterUpdatedRows = (tableRows: DataTableRow[]) => {
        const updatedRows = tableRows.filter((tableRow) => {
            const originRow = dataTable.rows.find((f) => f.id === tableRow.id);

            if (originRow) {
                const hasAnyCellDifference = originRow.cells.some(
                    (originCell) => {
                        const tableCell = tableRow.cells.find(
                            (cell) => cell.id === originCell.id
                        );

                        if (tableCell) {
                            const tableCellAnswer = tableCell.answerText ?? '';
                            const originalTableCellAnswer =
                                originCell.answerText ?? '';
                            return (
                                tableCellAnswer !== originalTableCellAnswer ||
                                originRow.isVisible !== tableRow.isVisible
                            );
                        } else {
                            return false;
                        }
                    }
                );

                return hasAnyCellDifference;
            }

            return false;
        });

        return updatedRows;
    };

    const saveTableRowsChanges = (tableRows: DataTableRow[]) => {
        return () => {
            const celanRows = clearRowsFromCellProperties(tableRows);
            const updatedRows = filterUpdatedRows(celanRows);

            if (!updatedRows.length) {
                pendingUpdateRef.current = false;
                return;
            }

            if (
                Object.keys(apiRef?.current?.state?.editRows ?? [])?.length > 0
            ) {
                pendingUpdateRef.current = true;
                return;
            }

            axios
                .put(
                    `/api/dataRequestProjects/${props.projectId}/dataTables/${props.dataTable.id}/dataTableRows`,
                    {
                        targetViewId: props.selectedTableViewId,
                        rows: updatedRows,
                    }
                )
                .then((response) => {
                    if (response.status !== 200) {
                        const message = (response as any)?.response?.data
                            ?.detail;
                        createNotification(
                            message ?? 'An error occurred while updating row',
                            'error'
                        );
                    }
                    setIsForceUpdate(true);
                    props.updateProjectsData();
                })
                .finally(() => {
                    isUpdateRowsPending.current = false;

                    if (isAddNewRowPending.current === true) {
                        handleAddNewRow();
                    }
                });
            pendingUpdateRef.current = false;
        };
    };

    const debounceUpdateDataTableRows = async (tableRows: DataTableRow[]) => {
        isUpdateRowsPending.current = true;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
            const save = saveTableRowsChanges(tableRows);

            const response = await wrapInPromise(
                save,
                2000,
                abortControllerRef.current.signal
            );
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (
            JSON.stringify(dataTable?.rows) !==
                JSON.stringify(props.dataTable.rows) ||
            JSON.stringify(dataTable?.columns) !==
                JSON.stringify(props.dataTable.columns) ||
            props.dataTable.progressStatus !== dataTable?.progressStatus ||
            isForceUpdate
        ) {
            updateTableData();
            setDataTable((prevDataTable) => {
                const preservedFreezePanesSettings = prevDataTable?.views?.find(
                    (view) => view.id === props.selectedTableViewId
                )?.freezePanesSettings;
                const preservedTableFreezePanesSettings = prevDataTable?.freezePanesSettings;
                
                const updatedViews = props.dataTable.views.map((view) => {
                    if (view.id === props.selectedTableViewId && preservedFreezePanesSettings) {
                        return {
                            ...view,
                            freezePanesSettings: preservedFreezePanesSettings,
                        };
                    }
                    return view;
                });
                
                return {
                    ...props.dataTable,
                    views: updatedViews,
                    freezePanesSettings: preservedTableFreezePanesSettings ?? props.dataTable.freezePanesSettings,
                };
            });
            setIsForceUpdate(false);
        }
    }, [props.dataTable, props.selectedTableViewId]);

    useEffect(() => {
        if (!props.dataTable) return;

        const currentView = props.selectedTableViewId
            ? props.dataTable.views.find(
                  (v) => v.id === props.selectedTableViewId
              )
            : null;

        let initialWidths: ResizedColumnDefinition[] = [];

        if (currentView) {
            initialWidths = currentView.viewColumns
                .filter((vc) => vc.width != null)
                .map((vc) => ({
                    columnId: vc.customColumnId,
                    width: vc.width,
                }));
        } else {
            initialWidths = props.dataTable.columns
                .filter((col) => col.width != null)
                .map((col) => ({
                    columnId: col.customColumnId,
                    width: col.width,
                }));
        }

        if (!initialWidths.length && props.columnWidthOverrides?.length) {
            initialWidths = props.columnWidthOverrides;
        }

        setResizedColumns(initialWidths);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.dataTable?.id, props.selectedTableViewId]);

    useEffect(() => {
        if (!props.isAdminView && props.selectedTableViewId) {
            props.markCellsAsSeen(
                props.projectId,
                props.dataTable.id,
                props.selectedTableViewId
            );
        }

        return () => {
            if (!props.isAdminView && !props.dataTable.isSnapshot) {
                const cellsToReset = tableData.flatMap((tableRow) =>
                    tableRow.cells.map(
                        (cell): ResetCellInfo => ({
                            customColumnId: cell.customColumnId,
                            customRowId: tableRow.customRowId,
                        })
                    )
                );
                resetCellNotifications(cellsToReset);
            }
        };
    }, [dataTable]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'F11') {
                event.preventDefault();
                if (fullScreenEnabled) {
                    exitFullScreen();
                } else {
                    enterFullScreen();
                }
            }
            if (event.key === 'Escape' && fullScreenEnabled) {
                exitFullScreen();
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [fullScreenEnabled]);

    useEffect(() => {
        if (props.selectedTableViewId && !isMarkAsCompleteModified) {
            const selectedTableView = getCurrentTableView();
            const markAsComplete = props.isAdminView
                ? selectedTableView?.isComplete
                : selectedTableView?.isReadyForReview;

            setMarkAsCompleteState(markAsComplete);
        }
    }, [props]);

    const handleAddNewRow = () => {
        if (isRequestPending && !isAddNewRowPending) return;
        setIsRequestPending(true);
        if (isUpdateRowsPending.current === true) {
            isAddNewRowPending.current = true;
            return;
        }

        const ids = props.dataTable.rows.map((x) => x.customRowId);
        const newTableRowId =
            CustomIdConstants.getNextDataTableRowCustomId(ids);
        axios
            .post(
                `/api/dataRequestProjects/${props.projectId}/dataTables/${props.dataTable.id}/dataTableRows`,
                { customDataTableRowId: newTableRowId }
            )
            .then((response) => {
                if (response.status === 200) {
                    props.updateProjectsData();
                } else {
                    const message = (response as any)?.response?.data?.detail;
                    createNotification(
                        message ?? 'An error occurred while adding row',
                        'error'
                    );
                }
            })
            .finally(() => {
                setIsRequestPending(false);
                isAddNewRowPending.current = false;
            });
    };

    const renderAddNewRow = () => (
        <ButtonLoader
            onClick={handleAddNewRow}
            buttonText={'+ Add New Row'}
            className={'btn btn-primary'}
            loaderButtonText={''}
            isLoading={isRequestPending}
            disabled={
                isRequestPending ||
                props.dataTable.progressStatus ===
                    DataTableProgressStatus.Complete ||
                isReadonlyAccess()
            }></ButtonLoader>
    );

    const isMarkAsCompleteDisabled = () => {
        return (
            getInitialMarkAsCompleteState() === markAsCompleteState ||
            !isViewAnswersComplete() ||
            isReadonlyAccess()
        );
    };

    const renderSaveAndClose = () => (
        <ButtonLoader
            onClick={() => {
                if (!isMarkAsCompleteDisabled()) {
                    updateMarkAsCompleteState();
                }
                props.onSaveAndCloseClick();
            }}
            buttonText={'Save & Close'}
            className={'btn btn-primary'}
            loaderButtonText={''}
            isLoading={isRequestPending}
            disabled={false}></ButtonLoader>
    );

    const customActionColumn = DataTableFormActionColumn({
        setRows: updateRows,
        gridApiRef: apiRef,
        getRowId: getRowId,
        projectId: props.projectId,
        dataTableId: props.dataTable.id,
        updateProjectsData: props.updateProjectsData,
        isAdmin: props.isAdminView,
    });

    const visibilityColumn: GridColDef = {
        field: 'isVisible',
        type: 'actions',
        headerName: 'Visible',
        minWidth: 70,
        maxWidth: 100,
        headerAlign: 'left',
        cellClassName: 'switcher-cell item-visibility',
        getActions: (value: any) => {
            const id = value.id;
            return [
                <ReactSwitch
                    offColor="#d92550"
                    checked={value.row?.isVisible ?? false}
                    onChange={(checked) => {
                        updateRows((oldRows) =>
                            oldRows.map((row) =>
                                getRowId(row) === id
                                    ? { ...row, isVisible: checked }
                                    : row
                            )
                        );
                    }}
                    height={22}
                />,
            ];
        },
    };

    const allColumns = (): GridColDef[] => {
        let result: GridColDef[] = [];

        const showVisibility =
            props.isAdminView &&
            !isReadonlyAccess() &&
            !props.isReadonlyMode;
        if (showVisibility) {
            result.push(visibilityColumn);
        }

        result = result.concat(dataColumns());

        const showAction = !isReadonlyAccess() && !props.isReadonlyMode;
        if (showAction) {
            result.push(customActionColumn);
        }

        return result;
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false } as unknown as DataTableRow;
        const updatedRows = tableData.map((row) => {
            if (getRowId(row) === getRowId(newRow)) {
                return updatedRow;
            }
            return row;
        });
        setTableData([...updatedRows]);
        debounceUpdateDataTableRows([...updatedRows]);
        return newRow;
    };

    const paginatedRows = useMemo(() => {
        const start = paginationModel.page * paginationModel.pageSize;
        return tableData.slice(start, start + paginationModel.pageSize);
    }, [tableData, paginationModel]);

    const handleTableViewStatusChange = (
        newStatus: DataTableViewProgressStatus
    ) => {
        const currentTableView = getCurrentTableView();
        setIsStatusUpdateInProcess(true);
        axios
            .put(
                `api/dataRequestProjects/${props.projectId}/dataTables/${props.dataTable.id}/dataTableViews/${currentTableView.id}/progressStatus`,
                { progressStatus: newStatus }
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while updating view status. Please, try again',
                        'error'
                    );
                } else {
                    props.updateProjectsData();
                }
            })
            .finally(() => {
                setIsStatusUpdateInProcess(false);
            });
    };

    const updateMarkAsCompleteState = () => {
        setIsLoading(true);

        const updateUrl = props.isAdminView
            ? `/isComplete?${objectToQueryString({
                  isComplete: markAsCompleteState,
              })}`
            : `/isReadyForApproval?${objectToQueryString({
                  isReadyForApproval: markAsCompleteState,
              })}`;

        axios
            .put(
                `api/dataRequestProjects/${props.projectId}/dataTables/${props.dataTable.id}/dataTableViews/${props.selectedTableViewId}${updateUrl}`
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while updating complete status',
                        'error'
                    );
                    setMarkAsCompleteState(!markAsCompleteState);
                } else {
                    props.updateProjectsData();
                }
            })
            .finally(() => {
                setIsLoading(false);
                setIsMarkAsCompleteModified(false);
            });
    };

    const getInitialMarkAsCompleteState = () => {
        const selectedTableView = getCurrentTableView();
        const initialMarkAsCompleteState = props.isAdminView
            ? selectedTableView.isComplete
            : selectedTableView.isReadyForReview;

        return initialMarkAsCompleteState;
    };

    const isViewAnswersComplete = () => {
        const currentView = getCurrentTableView();
        const includedColumnCustomIds = currentView.viewColumns.flatMap(
            (viewColumn) => viewColumn.customColumnId
        );
        const visibleCells = props.dataTable.rows
            .filter(
                (row) =>
                    row.isVisible &&
                    isRowAvailableByViewFilter(row, currentView) &&
                    !row.isRemovalRequested
            )
            .flatMap((row) => {
                const cells = row.cells.filter((cell) => {
                    const targetColumn = props.dataTable.columns.find(
                        (column) =>
                            column.customColumnId === cell.customColumnId
                    );

                    const allViewCustomColumnIds =
                        props.dataTable.views.flatMap((view) =>
                            view.viewColumns.map(
                                (viewColumn) => viewColumn.customColumnId
                            )
                        );
                    const isAvailableByLogic = isCellAvailableByLogic(
                        targetColumn,
                        row,
                        allViewCustomColumnIds,
                        props.dataTable.columns
                    );
                    return (
                        includedColumnCustomIds.some(
                            (includedColumnCustomId) =>
                                includedColumnCustomId === cell.customColumnId
                        ) && isAvailableByLogic
                    );
                });

                return cells;
            });

        const fileTypeName = AnswerTypeVariableNamesList.find(
            (f) => f.id === AnswerTypeVariable.File
        )?.name;

        const isVisibleCellsAnswerComplete =
            visibleCells.every((cell) => {
                const column = props.dataTable.columns.find(
                    (c) => c.customColumnId === cell.customColumnId
                );
                if (column?.answerType === fileTypeName) return true;
                return (
                    cell.answerText !== undefined &&
                    cell.answerText !== null &&
                    cell.answerText !== ''
                );
            }) &&
            currentView.viewColumns.length > 0 &&
            visibleCells.length > 0;

        return isVisibleCellsAnswerComplete;
    };

    const memoizedColumns = useMemo(() => allColumns(), [
        dataTable,
        tableData,
        columnResized,
        props.columnWidthOverrides,
        props.isAdminView,
        props.selectedTableViewId,
    ]);

    const getFreezeCellColumnIndex = useCallback((field: string): number => {
        return memoizedColumns.findIndex(col => col.field === field);
    }, [memoizedColumns]);

    const getFreezeCellRowIndex = useCallback((rowId: any): number => {
        return paginatedRows.findIndex(row => getRowId(row) === rowId);
    }, [paginatedRows]);

    const handleFreezeCellSelection = useCallback((cell: GridCellParams | null) => {
        setSelectedFreezeCell(cell);
    }, []);

    const handleFreezeDropdownChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const selectedOption = event.target.options[event.target.selectedIndex];
        const label = selectedOption?.text || 'Freeze Panes';
        if (value === 'unfreeze') {
            setFreezeDropdownLabel('Freeze Panes');
        } else {
            setFreezeDropdownLabel(label);
        }
        if (value === 'unfreeze') {
            if (selectedFreezeCell) {
                const rowIndex = getFreezeCellRowIndex(selectedFreezeCell.id);
                const columnIndex = getFreezeCellColumnIndex(selectedFreezeCell.field);
                let viewportOffset: { top: number; left: number } | undefined;
                if (rowIndex >= 0 && columnIndex >= 0) {
                    const frozenCornerRowIndex = frozenRowStart > 0 ? frozenRowStart : 0;
                    const frozenCornerColumnIndex = frozenColumnStart > 0 ? frozenColumnStart : 0;
                    viewportOffset = {
                        top: rowIndex - frozenCornerRowIndex,
                        left: columnIndex - frozenCornerColumnIndex,
                    };
                }
                setFreezeScrollToCell({
                    id: selectedFreezeCell.id,
                    field: selectedFreezeCell.field,
                    viewportOffset,
                });
            }
            setFrozenColumnStart(0);
            setFrozenColumnCount(0);
            setFrozenRowStart(0);
            setFrozenRowCount(0);
            setScrollableColumnStart(undefined);
            setScrollableRowStart(undefined);
        } else if (value === 'freeze-at-selection') {
            if (!selectedFreezeCell) return;
            const visibleRanges = capturedVisibleRanges || (getVisibleRangesRef.current ? getVisibleRangesRef.current() : null);
            if (!visibleRanges) return;
            const columnIndex = getFreezeCellColumnIndex(selectedFreezeCell.field);
            const rowIndex = getFreezeCellRowIndex(selectedFreezeCell.id);
            if (columnIndex >= 0 && rowIndex >= 0) {
                const { firstColumnIndex, firstRowIndex } = visibleRanges;
                const freezeColumnEnd = columnIndex - 1;
                const columnsToFreeze = freezeColumnEnd >= firstColumnIndex ? freezeColumnEnd - firstColumnIndex + 1 : 0;
                const freezeRowEnd = rowIndex - 1;
                const rowsToFreeze = freezeRowEnd >= firstRowIndex ? freezeRowEnd - firstRowIndex + 1 : 0;
                if (columnsToFreeze > 0) {
                    setFrozenColumnStart(firstColumnIndex);
                    setFrozenColumnCount(columnsToFreeze);
                } else {
                    setFrozenColumnStart(0);
                    setFrozenColumnCount(0);
                }
                if (rowsToFreeze > 0) {
                    setFrozenRowStart(firstRowIndex);
                    setFrozenRowCount(rowsToFreeze);
                } else {
                    setFrozenRowStart(0);
                    setFrozenRowCount(0);
                }
                setScrollableColumnStart(columnIndex);
                setScrollableRowStart(rowIndex);
            }
            setCapturedVisibleRanges(null);
        } else if (value === 'up-to-column-1') {
            setFrozenColumnStart(0);
            setFrozenColumnCount(1);
            setScrollableColumnStart(1);
            setFrozenRowStart(0);
            setFrozenRowCount(0);
            setScrollableRowStart(undefined);
            setCapturedVisibleRanges(null);
        } else if (value === 'up-to-row-1') {
            setFrozenRowStart(0);
            setFrozenRowCount(1);
            setScrollableRowStart(1);
            setFrozenColumnStart(0);
            setFrozenColumnCount(0);
            setScrollableColumnStart(undefined);
            setCapturedVisibleRanges(null);
        } else if (value === 'up-to-column') {
            if (!selectedFreezeCell) return;
            const columnIndex = getFreezeCellColumnIndex(selectedFreezeCell.field);
            if (columnIndex > 0) {
                setFrozenColumnStart(0);
                setFrozenColumnCount(columnIndex);
                setScrollableColumnStart(columnIndex);
            } else if (columnIndex === 0) {
                setFrozenColumnStart(0);
                setFrozenColumnCount(0);
                setScrollableColumnStart(undefined);
            }
            setFrozenRowStart(0);
            setFrozenRowCount(0);
            setScrollableRowStart(undefined);
            setCapturedVisibleRanges(null);
        } else if (value === 'up-to-row') {
            if (!selectedFreezeCell) return;
            const rowIndex = getFreezeCellRowIndex(selectedFreezeCell.id);
            if (rowIndex > 0) {
                setFrozenRowStart(0);
                setFrozenRowCount(rowIndex);
                setScrollableRowStart(rowIndex);
            } else if (rowIndex === 0) {
                setFrozenRowStart(0);
                setFrozenRowCount(0);
                setScrollableRowStart(undefined);
            }
            setFrozenColumnStart(0);
            setFrozenColumnCount(0);
            setScrollableColumnStart(undefined);
            setCapturedVisibleRanges(null);
        }
    }, [selectedFreezeCell, capturedVisibleRanges, getFreezeCellColumnIndex, getFreezeCellRowIndex, frozenRowStart, frozenColumnStart]);

    const freezeDropdownValue = useMemo(() => {
        if (frozenColumnCount > 0 || frozenRowCount > 0) return 'frozen';
        return 'none';
    }, [frozenColumnCount, frozenRowCount]);

    const upToRowText = useMemo(() => {
        if (!selectedFreezeCell) return 'Up To Row...';
        const rowIndex = getFreezeCellRowIndex(selectedFreezeCell.id);
        if (rowIndex >= 0) return `Up To Row ${rowIndex}`;
        return 'Up To Row...';
    }, [selectedFreezeCell, getFreezeCellRowIndex]);

    const upToColumnText = useMemo(() => {
        if (!selectedFreezeCell) return 'Up To Column...';
        const columnIndex = getFreezeCellColumnIndex(selectedFreezeCell.field);
        if (columnIndex >= 0) return `Up To Column ${columnIndex}`;
        return 'Up To Column...';
    }, [selectedFreezeCell, getFreezeCellColumnIndex]);

    const gridSlotProps = useMemo(() => ({
        baseSelect: {
            MenuProps: {
                PaperProps: {
                    sx: {
                        '& .MuiMenuItem-root': {
                            minHeight: '36px',
                        },
                    },
                },
            },
        },
    }), []);

    const handleGetVisibleRanges = useCallback((getRanges: () => { firstColumnIndex: number; lastColumnIndex: number; firstRowIndex: number; lastRowIndex: number }) => {
        getVisibleRangesRef.current = getRanges;
    }, []);

    const handleGridCellClick = useCallback((params: any) => {
        if (!props.isAdminView && !props.dataTable.isSnapshot) {
            const cellToReset: ResetCellInfo = {
                customColumnId: params.field,
                customRowId: params.id as string,
            };
            resetCellNotifications([cellToReset]);
        }
    }, [props.isAdminView, props.dataTable?.isSnapshot]);

    const handleGridColumnResize = useCallback((params: any) => {
        const roundedWidth = Math.round(params.width);
        const field = params.colDef.field;
        setResizedColumns((prev) => {
            const existingIndex = prev.findIndex(
                (col) => col.columnId === field
            );
            let next: ResizedColumnDefinition[];
            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                    columnId: field,
                    width: roundedWidth,
                };
                next = updated;
            } else {
                next = [
                    ...prev,
                    {
                        columnId: field,
                        width: roundedWidth,
                    },
                ];
            }
            debouncedSaveColumnWidth(field, roundedWidth);
            return next;
        });
        props.updateColumnWidth?.(
            props.dataTable.id,
            field,
            roundedWidth
        );
    }, [debouncedSaveColumnWidth, props.dataTable?.id, props.updateColumnWidth]);

    const handleFreezeDropdownOpen = useCallback((event: React.MouseEvent<HTMLSelectElement>) => {
        if (getVisibleRangesRef.current) {
            setCapturedVisibleRanges(getVisibleRangesRef.current());
        }
    }, []);

    useEffect(() => {
        if (freezeScrollToCell && frozenColumnCount === 0 && frozenRowCount === 0) {
            const timeoutId = setTimeout(() => {
                setFreezeScrollToCell(null);
            }, 200);
            return () => clearTimeout(timeoutId);
        }
    }, [freezeScrollToCell, frozenColumnCount, frozenRowCount]);

    useEffect(() => {
        if (scrollableColumnStart !== undefined && frozenColumnStart >= 0 && frozenColumnCount >= 0) {
            const newScrollableStart = frozenColumnStart + frozenColumnCount;
            if (newScrollableStart !== scrollableColumnStart) {
                setScrollableColumnStart(newScrollableStart);
            }
        }
    }, [frozenColumnStart, frozenColumnCount, scrollableColumnStart]);

    useEffect(() => {
        if (scrollableRowStart !== undefined && frozenRowStart >= 0 && frozenRowCount >= 0) {
            const newScrollableStart = frozenRowStart + frozenRowCount;
            if (newScrollableStart !== scrollableRowStart) {
                setScrollableRowStart(newScrollableStart);
            }
        }
    }, [frozenRowStart, frozenRowCount, scrollableRowStart]);

    useEffect(() => {
        if (isInitialLoadRef.current) {
            return;
        }
        const settings: FreezePanesSettings = {
            frozenColumnCount,
            frozenRowCount,
            frozenColumnStart,
            frozenRowStart,
            scrollableColumnStart,
            scrollableRowStart,
        };

        if (props.selectedTableViewId) {
            setDataTable((prevDataTable) => {
                const baseTable = prevDataTable || props.dataTable;
                if (!baseTable) return prevDataTable;
                const updatedViews = baseTable.views.map((view) => {
                    if (view.id === props.selectedTableViewId) {
                        return {
                            ...view,
                            freezePanesSettings: settings,
                        };
                    }
                    return view;
                });
                return {
                    ...baseTable,
                    views: updatedViews,
                };
            });
            props.updateFreezePanes?.(props.dataTable.id, props.selectedTableViewId, settings);
            debouncedSaveFreezePanesSettings(settings);
        } else {
            setDataTable((prevDataTable) => {
                const baseTable = prevDataTable || props.dataTable;
                if (!baseTable) return prevDataTable;
                return {
                    ...baseTable,
                    freezePanesSettings: settings,
                };
            });
            props.updateDataTableFreezePanes?.(props.dataTable.id, settings);
            debouncedSaveDataTableFreezePanesSettings(settings);
        }
    }, [frozenColumnCount, frozenRowCount, frozenColumnStart, frozenRowStart, scrollableColumnStart, scrollableRowStart, props.selectedTableViewId, debouncedSaveFreezePanesSettings, debouncedSaveDataTableFreezePanesSettings]);

    const renderViewStautsBar = () => {
        const selectedTableView = getCurrentTableView();

        const statusLabel = props.isAdminView ? (
            <>
                <div className="table-view-progress-status-dropdown-wrapper">
                    <div className="status">{'Status: '}</div>
                    <select
                        disabled={isStatusUpdateInProcess}
                        value={selectedTableView.progressStatus}
                        style={{
                            color: TableViewProgressStatusList.find(
                                (x) =>
                                    x.id ==
                                    (selectedTableView.progressStatus ?? 0)
                            ).color,
                        }}
                        onChange={(event) => {
                            const value = Number(event.target.value);
                            handleTableViewStatusChange(value);
                        }}
                        name="table-view-progress-status"
                        className="table-view-progress-status-dropdown form-control">
                        {TableViewProgressStatusList.map((item, index) => (
                            <option
                                style={{ color: item.color }}
                                key={index + item.id + item.name}
                                value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>
            </>
        ) : (
            <div className="table-view-progress-status-dropdown-wrapper">
                <div className="status">{'Status: '}</div>
                <div
                    style={{
                        color: TableViewProgressStatusList.find(
                            (x) => x.id == selectedTableView.progressStatus
                        ).color,
                        fontFamily: 'SegoeUI-Bold',
                    }}>
                    {
                        TableViewProgressStatusList.find(
                            (x) => x.id == selectedTableView.progressStatus
                        ).name
                    }
                </div>
            </div>
        );

        return (
            <>
                {statusLabel}
                {props.areActionButtonsHidden ? (
                    <></>
                ) : (
                    <div className="mark-as-complete-section">
                        <input
                            disabled={
                                !isViewAnswersComplete() || isReadonlyAccess()
                            }
                            checked={markAsCompleteState}
                            type="checkbox"
                            onChange={(event) => {
                                setMarkAsCompleteState(event.target.checked);
                                setIsMarkAsCompleteModified(true);
                            }}></input>
                        <label>{'Mark as Complete'}</label>
                        <ButtonLoader
                            buttonText={'Save Status'}
                            loaderButtonText={''}
                            disabled={isMarkAsCompleteDisabled()}
                            isLoading={isLoading}
                            onClick={updateMarkAsCompleteState}
                            className="btn btn-primary"
                        />
                    </div>
                )}
            </>
        );
    };

    const renderTableStatus = () => (
        <div className="table-view-progress-status-dropdown-wrapper">
            <div className="status">{'Status: '}</div>
            <div
                style={{
                    color: DataTableProgressStatusList.find(
                        (x) => x.id == (props.dataTable.progressStatus ?? 0)
                    ).color,
                    fontFamily: 'SegoeUI-Bold',
                }}>
                {
                    DataTableProgressStatusList.find(
                        (x) => x.id == (props.dataTable.progressStatus ?? 0)
                    ).name
                }
            </div>
        </div>
    );

    const exitFullScreen = () => {
        setFullScreenEnabled(false);
        const header = document.querySelector(
            '.app-header.header-shadow'
        ) as HTMLElement;
        if (header) {
            header.style.display = 'flex';
        }
        const leftMenu = document.querySelector(
            '.app-sidebar.sidebar-shadow'
        ) as HTMLElement;
        if (leftMenu) {
            leftMenu.style.display = 'flex';
        }
    };

    const enterFullScreen = () => {
        setFullScreenEnabled(true);
        const header = document.querySelector(
            '.app-header.header-shadow'
        ) as HTMLElement;
        if (header) {
            header.style.display = 'none';
        }
        const leftMenu = document.querySelector(
            '.app-sidebar.sidebar-shadow'
        ) as HTMLElement;
        if (leftMenu) {
            leftMenu.style.display = 'none';
        }
    };

    useEffect(() => {
        const totalPages = Math.ceil(
            tableData.length / paginationModel.pageSize
        );
        if (paginationModel.page >= totalPages && totalPages > 0) {
            setPaginationModel((prev) => ({
                ...prev,
                page: totalPages - 1,
            }));
        }
    }, [tableData.length, paginationModel.pageSize]);

    return (
        <div
            className={
                fullScreenEnabled
                    ? 'data-table-view-host full-screen'
                    : 'data-table-view-host'
            }>
            <div className="status-tool-bar">
                <div className="status-tool-bar-left">
                    {props.isAdminView && !props.isReadonlyMode && (
                        <div>
                            <input
                                hidden={true}
                                type="file"
                                name="file"
                                ref={fileInputRef}
                                accept=".xlsx, .xls"
                                id="excel-file-input"
                                className="upload-file-input"
                                multiple={false}
                                onChange={handleFileChange}
                            />
                            <Button
                                onClick={handleButtonClick}
                                color="primary"
                                disabled={isReadonlyAccess()}>
                                Import
                            </Button>
                        </div>
                    )}
                    <div className="freeze-panes-dropdown-wrapper">
                        <select
                            value=""
                            onClick={handleFreezeDropdownOpen as any}
                            onChange={handleFreezeDropdownChange}
                            name="freeze-panes"
                            className="freeze-panes-dropdown form-control">
                            <option value="" disabled hidden>
                                {freezeDropdownLabel}
                            </option>
                            <option value="unfreeze">Unfreeze</option>
                            {selectedFreezeCell && frozenColumnCount === 0 && frozenRowCount === 0 && (
                                <option value="freeze-at-selection">Freeze at selection</option>
                            )}
                            {!selectedFreezeCell && (
                                <option value="up-to-column-1">Up To Column 1</option>
                            )}
                            {selectedFreezeCell && (
                                <option value="up-to-column">{upToColumnText}</option>
                            )}
                            {!selectedFreezeCell && (
                                <option value="up-to-row-1">Up To Row 1</option>
                            )}
                            {selectedFreezeCell && (
                                <option value="up-to-row">{upToRowText}</option>
                            )}
                        </select>
                    </div>
                </div>

                {props.isReadonlyMode ? (
                    <></>
                ) : props.selectedTableViewId ? (
                    renderViewStautsBar()
                ) : (
                    renderTableStatus()
                )}
                {props.areActionButtonsHidden ||
                props.isFullScreenButtonHidden ? (
                    <> </>
                ) : (
                    <div
                        className="full-screen-button"
                        onClick={() => {
                            fullScreenEnabled
                                ? exitFullScreen()
                                : enterFullScreen();
                        }}>
                        {fullScreenEnabled ? (
                            <FontAwesomeIcon icon={faCompressAlt} />
                        ) : (
                            <FontAwesomeIcon icon={faExpandAlt} />
                        )}
                    </div>
                )}
            </div>
            <div className="freeze-pane-grid-wrapper">
                <FreezePaneDataGrid
                    mainApiRef={apiRef}
                    columns={memoizedColumns}
                    rows={paginatedRows}
                    frozenColumnCount={frozenColumnCount}
                    frozenRowCount={frozenRowCount}
                    frozenColumnStart={frozenColumnStart}
                    frozenRowStart={frozenRowStart}
                    scrollableColumnStart={scrollableColumnStart}
                    scrollableRowStart={scrollableRowStart}
                    onFrozenColumnCountChange={setFrozenColumnCount}
                    onFrozenRowCountChange={setFrozenRowCount}
                    onCellSelectionChange={handleFreezeCellSelection}
                    onGetVisibleRanges={handleGetVisibleRanges}
                    scrollToCell={freezeScrollToCell}
                    rowHeight={52}
                    columnHeaderHeight={34}
                    getRowId={getRowId}
                    processRowUpdate={processRowUpdate}
                    showCellVerticalBorder={true}
                    onCellClick={handleGridCellClick}
                    onColumnResize={handleGridColumnResize}
                    slotProps={gridSlotProps}
                />
            </div>
            <div className="data-table-pagination-wrapper">
                <TablePagination
                    component="div"
                    count={tableData.length}
                    page={paginationModel.page}
                    onPageChange={(_, page) =>
                        setPaginationModel((prev) => ({ ...prev, page }))
                    }
                    rowsPerPage={paginationModel.pageSize}
                    onRowsPerPageChange={(e) =>
                        setPaginationModel({
                            page: 0,
                            pageSize: parseInt(e.target.value, 10),
                        })
                    }
                    rowsPerPageOptions={[25, 50, 100]}
                />
            </div>
            {!props.isReadonlyMode && !props.areActionButtonsHidden && (
                <div className="bottom-button-wrapper">
                    {renderAddNewRow()}
                    {props.isSaveAndCloseVisible && renderSaveAndClose()}
                </div>
            )}
            {isFilePopupVisible && selectedCell && (
                <DataTableCellUploadPopup
                    projectId={props.projectId}
                    tableId={props.dataTable.id}
                    columnId={selectedCell.columnId}
                    rowId={selectedCell.rowId}
                    isReadonlyMode={props.isReadonlyMode || isReadonlyAccess()}
                    isAdminView={props.isAdminView}
                    close={() => setIsFilePopupVisible(false)}
                    onSuccessfulUpload={() => props.updateProjectsData()}
                />
            )}
        </div>
    );
};
